/**
 * Route definition for /home
 *
 * Uses TanStack Router's loader to prefetch active listings data server-side.
 * Fetches current auction data before render for instant page display.
 *
 * @module routes/home
 * @category Routes
 */

import { createFileRoute } from '@tanstack/react-router'
import { toast } from 'react-toastify'
import { getListingsByMeta, ApiError } from '@/lib/api'
import HomeContainer from '@/containers/Listings'
import { createItemListSchema } from '@/utils/metadata'
import { setPageMetadata } from '@/utils/page-metadata'
import { t } from '@/i18n/translator'

/**
 * Loader function that runs before the component renders.
 *
 * Server-side: fetches active listings and returns them
 * Client-side: provides cached data if available, refetches if stale
 *
 * @returns {Promise<{initialAuctions: any[]}>}
 */
export const Route = createFileRoute('/$locale/home')({
  /**
   * Preloads the listings data before component mount.
   * Supports SSR - data is fetched on server during initial render
   * and hydrated on client for seamless transition.
   */
  loader: async () => {
    try {
      // Fetch active listings using typed API
      const auctions = await getListingsByMeta()

      // Return data to be available via Route.useLoaderData()
      return {
        initialAuctions: auctions,
      }
    } catch (error) {
      // Log error, show toast, and propagate to error boundary
      console.error('Failed to load listings:', error)
      toast.error('Failed to load listings. Please try again.')
      throw error
    }
  },

  /**
   * The component that renders the home page.
   * Receives loader data via Route.useLoaderData() hook.
   */
  component: HomePage,
})

/**
 * Home page component.
 *
 * Receives preloaded listings data from the loader and passes it to
 * the HomeContainer. This eliminates the need for useEffect-based
 * data fetching and provides instant rendering.
 *
 * @returns {React.ReactElement}
 */
function HomePage() {
  // Get the preloaded data from the route loader
  const { initialAuctions } = Route.useLoaderData()

  // Build structured data for active listings
  const structuredData = initialAuctions && initialAuctions.length > 0
    ? createItemListSchema({
        title: t('meta.home.title'),
        description: t('meta.home.description'),
        url: 'https://stealthisnft.xyz/home',
        items: initialAuctions.slice(0, 20).map(auction => ({
          name: auction.title || `NFT #${auction.tokenId}`,
          description: auction.description || '',
          image: auction.img_ipfs || auction.image || '',
          url: auction.route_url || `https://stealthisnft.xyz/item/${auction.chain}/${auction.contract_address}/${auction.tokenId}`,
          price: auction.price,
          currency: 'SGB',
        }))
      })
    : undefined

  // Set metadata including dynamic JSON-LD
  setPageMetadata({
    title: t('meta.home.title'),
    description: t('meta.home.description'),
    ogImage: '',
    ogType: 'website',
    twitterCard: 'summary_large_image',
    jsonLd: structuredData || null,
  })

  return (
    <HomeContainer initialAuctions={initialAuctions} />
  )
}
